import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks whether a `position: sticky` element is currently stuck, by
 * comparing its live bounding box against its computed `top` offset on
 * scroll. Used purely for cosmetic changes (eg. showing an elevation
 * shadow) while stuck - the sticking itself is handled entirely by CSS.
 */
const useIsStuck = <T extends HTMLElement>(): [boolean, React.RefObject<T>] => {
  const ref = useRef<T>(null);
  const pendingFrame = useRef<number | null>(null);
  const [isStuck, setIsStuck] = useState(false);

  const handleScroll = useCallback(() => {
    if (pendingFrame.current !== null) {
      return;
    }
    pendingFrame.current = requestAnimationFrame(() => {
      pendingFrame.current = null;
      const node = ref.current;
      if (!node) {
        return;
      }
      const top = parseInt(window.getComputedStyle(node).top, 10);
      const nextIsStuck = node.getBoundingClientRect().top <= top;
      setIsStuck((prev) => (prev === nextIsStuck ? prev : nextIsStuck));
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (pendingFrame.current !== null) {
        cancelAnimationFrame(pendingFrame.current);
      }
    };
  }, [handleScroll]);

  return [isStuck, ref];
};

export default useIsStuck;
